import {
  INVENTORY_ADD,
  INVENTORY_DELETE,
  INVENTORY_EXPIRE_DUE,
  INVENTORY_FILE_REMOVE,
  INVENTORY_FILE_UPLOAD,
  INVENTORY_FILTER,
  INVENTORY_GROUPED_BY_NAME,
  INVENTORY_NUM_EXPIRED,
  INVENTORY_UPDATE,
  INVENTORY_TYPES,
  SUPPLIER_LIST,
  FILE_UPLOAD_URL,
  FILE_DOWNLOAD_URL,
  FILE_DELETE,
} from "../../../api/ApiEndpoints";
import api from "../../../interceptor/Interceptor";
import {
  fetchInventoryStart,
  fetchInventorySuccess,
  fetchInventoryFailure,
  addInventoryStart,
  addInventorySuccess,
  addInventoryFailure,
  editInventoryStart,
  editInventorySuccess,
  editInventoryFailure,
  deleteInventoryStart,
  deleteInventorySuccess,
  deleteInventoryFailure,
  fetchExpiryStatsStart,
  fetchExpiryStatsSuccess,
  fetchExpiryStatsFailure,
  fetchInventoryLookupsStart,
  fetchInventoryLookupsSuccess,
  fetchInventoryLookupsFailure,
} from "../../reducer/inventoryReducer/InventoryReducer";

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || error?.error || fallback;

// .................... List / Filter ...........................

// export const fetchInventory = (query) => async (dispatch) => {
//   dispatch(fetchInventoryStart());
//   try {
//     const url =
//       query && Object.keys(query).length > 0
//         ? `${INVENTORY_FILTER}?query=${encodeURIComponent(JSON.stringify(query))}`
//         : INVENTORY_GROUPED_BY_NAME;
//     const response = await api.get(url);
//     const itemsByName = response.data.items || {};
//     dispatch(fetchInventorySuccess(itemsByName));
//     return { success: true, itemsByName };
//   } catch (error) {
//     const message = extractErrorMessage(error, "Failed to fetch inventory");
//     dispatch(fetchInventoryFailure(message));
//     return { success: false, error: message };
//   }
// };


export const fetchInventory = (query, page = 1, pageSize = 10) => async (dispatch) => {
  dispatch(fetchInventoryStart());
  try {
    const base =
      query && Object.keys(query).length > 0
        ? `${INVENTORY_FILTER}?query=${encodeURIComponent(JSON.stringify(query))}`
        : INVENTORY_GROUPED_BY_NAME;
    const separator = base.includes("?") ? "&" : "?";
    const url = `${base}${separator}page=${page}&pageSize=${pageSize}`;
 
    const response = await api.get(url);
    const itemsByName = response.data.items || {};
    const pagination = response.data.pagination || { page, pageSize, totalItems: 0, totalPages: 1 };
    dispatch(fetchInventorySuccess({ itemsByName, pagination }));
    return { success: true, itemsByName, pagination };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to fetch inventory");
    dispatch(fetchInventoryFailure(message));
    return { success: false, error: message };
  }
};

// .................... Add / Edit / Delete ...........................

export const addInventoryItems = (newItem) => async (dispatch) => {
  dispatch(addInventoryStart());
  try {
    await api.post(INVENTORY_ADD, newItem);
    dispatch(addInventorySuccess());
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to add to inventory");
    dispatch(addInventoryFailure(message));
    return { success: false, error: message };
  }
};

export const editInventoryItem = (id, updatedItem) => async (dispatch) => {
  dispatch(editInventoryStart());
  try {
    await api.put(`${INVENTORY_UPDATE}/${id}`, updatedItem);
    dispatch(editInventorySuccess());
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to update inventory");
    dispatch(editInventoryFailure(message));
    return { success: false, error: message };
  }
};

export const deleteInventoryItems = (ids) => async (dispatch) => {
  dispatch(deleteInventoryStart());
  try {
    await api.post(INVENTORY_DELETE, { ids });
    dispatch(deleteInventorySuccess());
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to delete from inventory");
    dispatch(deleteInventoryFailure(message));
    return { success: false, error: message };
  }
};

// .................... Expiry stats ...........................

export const fetchExpiryStats = () => async (dispatch) => {
  dispatch(fetchExpiryStatsStart());
  try {
    const [dueRes, expiredRes] = await Promise.all([
      api.get(INVENTORY_EXPIRE_DUE),
      api.get(INVENTORY_NUM_EXPIRED),
    ]);
    const payload = {
      numDueToExpire: dueRes.data.numDueToExpire || 0,
      numExpired: expiredRes.data.numExpired || 0,
    };
    dispatch(fetchExpiryStatsSuccess(payload));
    return { success: true, ...payload };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to fetch expiry stats");
    dispatch(fetchExpiryStatsFailure(message));
    return { success: false, error: message };
  }
};


// .................... Add/Edit form lookups (types + suppliers) ...........
 
export const fetchInventoryLookups = () => async (dispatch) => {
  dispatch(fetchInventoryLookupsStart());
  try {
    const [typesRes, suppliersRes] = await Promise.all([
      api.get(INVENTORY_TYPES),
      api.get(SUPPLIER_LIST),
    ]);
    const payload = {
      types: typesRes.data.types || [],
      suppliers: suppliersRes.data.suppliers || [],
    };
    dispatch(fetchInventoryLookupsSuccess(payload));
    return { success: true, ...payload };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to fetch types and suppliers");
    dispatch(fetchInventoryLookupsFailure(message));
    return { success: false, error: message };
  }
};

// .................... Document DB records ...........................
// Records/clears a document reference on the inventory item row itself.
// No reducer state changes — documents are tracked locally per-row in
// InventoryDocuments, same as before.

export const recordInventoryFile = (itemId, docName, docCategory) => async () => {
  try {
    await api.post(INVENTORY_FILE_UPLOAD, { itemId, docName, docCategory });
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, `Failed to add ${docCategory} document to inventory item`);
    return { success: false, error: message };
  }
};

export const clearInventoryFile = (itemId, docCategory) => async () => {
  try {
    await api.post(INVENTORY_FILE_REMOVE, { itemId, docCategory });
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, `Failed to remove ${docCategory} document from inventory item`);
    return { success: false, error: message };
  }
};

// .................... S3 file operations ...........................
// Low-level building blocks: get a signed URL to view a file, upload a
// file to S3 via a signed URL, or delete a file from S3.

export const getDocumentDownloadUrl = (fileKey) => async () => {
  try {
    const response = await api.get(`${FILE_DOWNLOAD_URL}?fileKey=${encodeURIComponent(fileKey)}`);
    return { success: true, downloadUrl: response.data.downloadUrl };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to fetch document");
    return { success: false, error: message };
  }
};

export const putDocumentToS3 = (file, fileKey) => async () => {
  try {
    const uploadRes = await api.get(`${FILE_UPLOAD_URL}?fileName=${encodeURIComponent(fileKey)}&fileType=${file.type}`);
    const { uploadUrl } = uploadRes.data;

    const putResponse = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!putResponse.ok) throw new Error(`Upload failed with status ${putResponse.status}`);

    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to upload document");
    return { success: false, error: message };
  }
};

export const deleteDocumentFromS3 = (fileKey) => async () => {
  try {
    await api.post(`${FILE_DELETE}?fileKey=${encodeURIComponent(fileKey)}`);
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to delete document");
    return { success: false, error: message };
  }
};

// .................... Composed document workflows ...........................

export const viewInventoryDocument = (docSource) => async (dispatch, getState) => {
  const { organizationId } = getState().auth.user;
  const fileKey = "inventory-docs/" + organizationId + docSource;
  return dispatch(getDocumentDownloadUrl(fileKey));
};

export const uploadInventoryDocument = (itemId, file, docCategory) => async (dispatch, getState) => {
  if (!file || !docCategory) {
    return { success: false, error: "Please select a file and document type." };
  }

  const { organizationId } = getState().auth.user;
  const destination =
    docCategory + "_" + file.name.split(".").slice(0, -1).join("") + "_" + Date.now() + "." + file.name.split(".").pop();
  const fileKey = `inventory-docs/${organizationId}/chemical-docs/${destination}`;

  const uploadRes = await dispatch(putDocumentToS3(file, fileKey));
  if (!uploadRes.success) return uploadRes;

  const recordRes = await dispatch(recordInventoryFile(itemId, destination, docCategory));
  if (!recordRes.success) return recordRes;

  return { success: true, destination };
};

export const removeInventoryDocument = (itemId, docName, docCategory) => async (dispatch, getState) => {
  const { organizationId } = getState().auth.user;
  const fileKey = "inventory-docs/" + organizationId + "chemical-docs/" + docName;

  const deleteRes = await dispatch(deleteDocumentFromS3(fileKey));
  if (!deleteRes.success) return deleteRes;

  return dispatch(clearInventoryFile(itemId, docCategory));
};